import { Stores, ProgramCategory, MultimediaCategory } from "../types";
import { makeAutoObservable } from "mobx";
import api from "../services/api";

export class CategoryStore {
  stores: Stores;

  programCategories: ProgramCategory[] = [];

  multimediaCategories: MultimediaCategory[] = [];

  isLoading = false;

  constructor(stores: Stores) {
    this.stores = stores;

    makeAutoObservable(this);
  }

  getProgramCategories = async () => {
    this.isLoading = true;
    try {
      const programCategories = await api.getProgramCategories();
      this.setProgramCategories(programCategories);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  addProgramCategory = async (data: ProgramCategory) => {
    this.isLoading = true;
    try {
      const programCategories = await api.addProgramCategory(data);
      this.setProgramCategories(
        [programCategories, ...this.programCategories].sort(
          (a, b) => a.orderNumber - b.orderNumber
        )
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateProgramCategory = async (data: ProgramCategory) => {
    this.isLoading = true;
    try {
      let updatedProgramCategory = await api.updateProgramCategory(data);

      this.setProgramCategories(
        this.programCategories
          .map((category) =>
            category.id === updatedProgramCategory.id
              ? updatedProgramCategory
              : category
          )
          .sort((a, b) => a.orderNumber - b.orderNumber)
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateProgramCategoriesOrder = async (
    category: ProgramCategory,
    oldIndex: number,
    newIndex: number
  ) => {
    this.isLoading = true;
    try {
      // We add +1 because the virtualized table returns the index of the row and not the
      // episode number.
      const updated = await api.updateProgramCategoriesOrder(
        category,
        oldIndex + 1,
        newIndex + 1
      );
      if (!updated) {
        throw new Error("Could not update order number");
      }
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  deleteProgramCategory = async (data: ProgramCategory) => {
    this.isLoading = true;
    try {
      await api.deleteProgramCategory(data);
      this.setProgramCategories(
        this.programCategories.filter((category) => category.id !== data.id)
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getMultimediaCategories = async () => {
    this.isLoading = true;
    try {
      const multimediaCategories = await api.getMultimediaCategories();
      this.setMultimediaCategories(multimediaCategories);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  addMultimediaCategory = async (data: MultimediaCategory) => {
    this.isLoading = true;
    try {
      const multimediaCategories = await api.addMultimediaCategory(data);
      this.setMultimediaCategories(
        [multimediaCategories, ...this.multimediaCategories].sort(
          (a, b) => a.orderNumber - b.orderNumber
        )
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateMultimediaCategory = async (data: MultimediaCategory) => {
    this.isLoading = true;
    try {
      let updatedMultimediaCategory = await api.updateMultimediaCategory(data);

      this.setMultimediaCategories(
        this.multimediaCategories
          .map((category) =>
            category.id === updatedMultimediaCategory.id
              ? updatedMultimediaCategory
              : category
          )
          .sort((a, b) => a.orderNumber - b.orderNumber)
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateMultimediaCategoriesOrder = async (
    category: MultimediaCategory,
    oldIndex: number,
    newIndex: number
  ) => {
    this.isLoading = true;
    try {
      // We add +1 because the virtualized table returns the index of the row and not the
      // episode number.
      const updated = await api.updateMultimediaCategoriesOrder(
        category,
        oldIndex + 1,
        newIndex + 1
      );
      if (!updated) {
        throw new Error("Could not update order number");
      }
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  deleteMultimediaCategory = async (data: MultimediaCategory) => {
    this.isLoading = true;
    try {
      await api.deleteMultimediaCategory(data);
      this.setMultimediaCategories(
        this.multimediaCategories.filter((category) => category.id !== data.id)
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  // Setters

  setProgramCategories = (programCategories: ProgramCategory[]) => {
    this.programCategories = programCategories;
  };

  setMultimediaCategories = (multimediaCategories: MultimediaCategory[]) => {
    this.multimediaCategories = multimediaCategories;
  };
}
